/**
 * What the service worker does with each request. Kept free of browser globals so it can be
 * tested directly; `service-worker.ts` applies it.
 */

export const SHELL_CACHE_PREFIX = 'fh-shell-';
export const DATA_CACHE_PREFIX = 'fh-data-';
/** Holds who is signed in, so a restarted worker files responses under the right person. */
export const IDENTITY_CACHE = 'fh-identity';
export const IDENTITY_URL = '/__offline/identity';
/** Response header with the time a copy served offline was saved. */
export const SAVED_AT_HEADER = 'X-Offline-Saved-At';
/** Response header on the reply when nothing was saved for an offline request. */
export const OFFLINE_HEADER = 'X-Offline';
export const MAX_DATA_ENTRIES = 400;
export const MAX_DATA_BYTES = 2_000_000;

export type RequestKind = 'navigation' | 'asset' | 'data' | 'network';

/**
 * Sign-in, account and admin endpoints are always fetched live; so are exports and anything
 * not under the API.
 */
const LIVE_ONLY = [
  /\/auth\//,
  /\/account\//,
  /\/security(\/|$)/,
  /\/billing(\/|$)/,
  /\/platform\//,
  /\/settings\/domains/,
  /\/settings\/manifest\.webmanifest$/,
  /\/health(\/|$)/,
  /\/(export|download)s?(\/|$)/,
  /\.(pdf|csv|xlsx)$/,
];

export function classifyRequest(request: { url: string; method: string; mode?: string }, origin: string): RequestKind {
  const url = new URL(request.url);
  if (request.method !== 'GET' || url.origin !== origin) return 'network';
  if (request.mode === 'navigate') return 'navigation';
  if (url.pathname.startsWith('/api/')) {
    return LIVE_ONLY.some((pattern) => pattern.test(url.pathname)) ? 'network' : 'data';
  }
  if (url.pathname.startsWith('/assets/') || /\.(js|css|woff2?|png|svg|ico)$/.test(url.pathname)) {
    return 'asset';
  }
  return 'network';
}

/** Only complete JSON answers of a sensible size are kept for offline use. */
export function shouldKeep(response: { status: number; headers: { get(name: string): string | null } }): boolean {
  if (response.status !== 200) return false;
  if (!(response.headers.get('content-type') ?? '').includes('application/json')) return false;
  const length = Number(response.headers.get('content-length') ?? '0');
  return !length || length <= MAX_DATA_BYTES;
}

/** Each person's saved copies live apart, so one never sees another's on a shared device. */
export function dataCacheName(identity: string): string {
  return `${DATA_CACHE_PREFIX}${identity.replace(/[^A-Za-z0-9:-]/g, '')}`;
}

export function offlineMissBody(): string {
  return JSON.stringify({
    statusCode: 503,
    offline: true,
    message: "You're offline, and this hasn't been opened on this device yet.",
  });
}
